import React, { useEffect } from "react";
import { Search, ArrowRight, AlertTriangle } from "lucide-react";

/* Champ de recherche : l'icône vit dans une gouttière, le texte ne passe jamais dessous. */
export const Recherche = ({ valeur, onChange, placeholder = "Rechercher…", testid, largeur = 300 }) => (
  <div className="relative" style={{ width: largeur, maxWidth: "100%" }}>
    <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "#94A3B8" }} />
    <input
      className="input-field"
      style={{ paddingLeft: 44, paddingTop: 11, paddingBottom: 11 }}
      placeholder={placeholder}
      value={valeur}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
    />
    {valeur && (
      <button
        type="button"
        onClick={() => onChange("")}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-[var(--text)] text-sm"
        data-testid={testid ? `${testid}-vider` : undefined}
        title="Effacer"
      >
        ✕
      </button>
    )}
  </div>
);

/* Filtres en pastilles : un seul choix actif, avec le nombre d'éléments. */
export const Puces = ({ options, valeur, onChange, testid }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testid}>
    {options.map((o) => {
      const on = (valeur || "") === o.valeur;
      return (
        <button
          key={o.valeur || "tous"}
          type="button"
          onClick={() => onChange(o.valeur)}
          data-testid={o.testid}
          className="px-3.5 py-1.5 rounded-full text-xs transition-transform hover:-translate-y-0.5"
          style={{
            background: on ? "var(--primary)" : "#fff",
            color: on ? "#04202A" : "var(--muted)",
            border: `1px solid ${on ? "var(--primary)" : "var(--border)"}`,
            fontWeight: on ? 600 : 400,
          }}
        >
          {o.label}
          {o.nb === undefined || o.nb === null || o.nb === ""
            ? null
            : <span style={{ opacity: 0.65 }}> ({o.nb})</span>}
        </button>
      );
    })}
  </div>
);

export const GRAVITES = {
  rouge: { fond: "#FEF2F2", bord: "#FECACA", texte: "#B91C1C", puce: "#DC2626" },
  ambre: { fond: "#FFFBEB", bord: "#FDE68A", texte: "#B45309", puce: "#F59E0B" },
  bleu: { fond: "#EFF6FF", bord: "#BFDBFE", texte: "#1D4ED8", puce: "#3B82F6" },
  vert: { fond: "#F0FDF4", bord: "#BBF7D0", texte: "#15803D", puce: "#22C55E" },
  neutre: { fond: "#F8FAFC", bord: "#E2E8F0", texte: "#475569", puce: "#94A3B8" },
  // L'étiquette « Stage » de la Facturation (Badge ton="violet") s'affichait en bleu : le ton
  // n'existait pas (relevé le 02.11.2026).
  violet: { fond: "#F5F3FF", bord: "#DDD6FE", texte: "#6D28D9", puce: "#8B5CF6" },
};

export const Eyebrow = ({ children }) => (
  <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--muted)]">{children}</div>
);

export const Section = ({ titre, aide, action, children, testid }) => (
  <section className="space-y-4" data-testid={testid}>
    <div className="flex flex-wrap items-end gap-3 border-b pb-3" style={{ borderColor: "var(--border)" }}>
      <div>
        <h2 className="font-display text-lg md:text-lg tracking-tight">{titre}</h2>
        {aide && <p className="text-xs text-[var(--muted)] mt-0.5">{aide}</p>}
      </div>
      {/* min-w-0 + max-w-full : sur téléphone, une barre d'onglets passe à la ligne au lieu
          de sortir de l'écran (Attestations, recette du 08.10.2026). */}
      {action && <div className="ml-auto min-w-0 max-w-full">{action}</div>}
    </div>
    {children}
  </section>
);

/* Les couleurs des grandes sections du tableau de bord : chaque thème a la sienne (bandeau
   de titre teinté, pastille pleine), pour qu'on distingue d'un coup d'œil où commence et
   où finit chaque partie d'une longue page (école, 12.10.2026). */
export const TONS_BLOC = {
  eleves: { accent: "#2563EB", doux: "#EFF6FF", bord: "#DBEAFE", texte: "#1E40AF" },
  argent: { accent: "#059669", doux: "#ECFDF5", bord: "#A7F3D0", texte: "#065F46" },
  cours: { accent: "#0891B2", doux: "#ECFEFF", bord: "#A5F3FC", texte: "#155E75" },
  annexes: { accent: "#7C3AED", doux: "#F5F3FF", bord: "#DDD6FE", texte: "#5B21B6" },
  // Les cours d'essai, une section à eux (Jen, 01.11.2026) : la couleur des étoiles des avis.
  essais: { accent: "#D97706", doux: "#FFFBEB", bord: "#FDE68A", texte: "#92400E" },
  assistant: { accent: "#4338CA", doux: "#EEF2FF", bord: "#C7D2FE", texte: "#3730A3" },
  profs: { accent: "#EA580C", doux: "#FFF7ED", bord: "#FED7AA", texte: "#9A3412" },
  attestations: { accent: "#0D9488", doux: "#F0FDFA", bord: "#99F6E4", texte: "#115E59" },
  application: { accent: "#DB2777", doux: "#FDF2F8", bord: "#FBCFE8", texte: "#9D174D" },
  site: { accent: "#65A30D", doux: "#F7FEE7", bord: "#D9F99D", texte: "#3F6212" },
  messages: { accent: "#0284C7", doux: "#F0F9FF", bord: "#BAE6FD", texte: "#075985" },
  analyses: { accent: "#334155", doux: "#F1F5F9", bord: "#E2E8F0", texte: "#334155" },
};

/* L'en-tête d'un menu (école, 20.10.2026 : « aligner le look des en-têtes sur Attestations
   et Assistant IA ») : un bandeau à la couleur du menu, son icône, une ligne de contexte,
   le titre et une phrase qui dit à quoi sert l'écran. `droite` : les états ou chiffres
   clés (badges) ; `children` : les réglages de l'écran (saison, semestre, boutons), sur une
   ligne sous le texte. */
/* Le bandeau d'un menu : titre, une phrase courte au plus, et l'essentiel en 2 à 4 cases
   (`points` : [[Icône, « Titre », « une ligne »], …]) — Jen, 03.11.2026 : « simple, facile à
   lire, à l'essentiel ; là ça ne donne pas envie de lire ». */
export const EnTeteMenu = ({ ton, Icon, contexte, titre, texte, points, droite, children, testid }) => {
  const t = TONS_BLOC[ton] || TONS_BLOC.analyses;
  const n = (points || []).length;
  return (
    <div className="rounded-3xl border px-5 md:px-6 py-5" style={{ background: t.doux, borderColor: t.bord }}
      data-testid={testid}>
      <div className="flex flex-wrap items-center gap-4">
        {Icon && (
          <span className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
            style={{ background: t.accent, color: "#fff" }}>
            <Icon size={24} />
          </span>
        )}
        <div className="min-w-[14rem] flex-1">
          {contexte && (
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: t.texte }}>{contexte}</div>
          )}
          <h1 className="font-display text-2xl md:text-3xl tracking-tight" style={{ color: "#0F1B2D" }}>{titre}</h1>
          {texte && <p className="text-sm mt-1 max-w-3xl" style={{ color: t.texte }}>{texte}</p>}
        </div>
        {droite && <div className="flex flex-wrap gap-2 items-center">{droite}</div>}
      </div>
      {n > 0 && (
        <div className={`grid grid-cols-1 sm:grid-cols-2 ${n >= 4 ? "xl:grid-cols-4" : n === 3 ? "lg:grid-cols-3" : ""} gap-1.5 sm:gap-2 mt-3 sm:mt-4`}
          data-testid={testid ? `${testid}-points` : undefined}>
          {/* Sur téléphone : des lignes serrées, sans cadre ; plus large : des cases. */}
          {points.map(([Icone, quoi, detail]) => (
            <div key={quoi} className="flex items-start gap-2.5 min-w-0 py-0.5 sm:rounded-2xl sm:bg-white/80 sm:px-3.5 sm:py-2.5 border-0 sm:border"
              style={{ borderColor: t.bord }}>
              {Icone && <Icone size={16} className="mt-0.5 shrink-0" style={{ color: t.accent }} />}
              <div className="min-w-0">
                <div className="text-[11px] font-semibold uppercase tracking-[0.12em]" style={{ color: t.texte }}>{quoi}</div>
                <div className="text-[13px] leading-snug" style={{ color: "#0F1B2D" }}>{detail}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      {children && <div className="flex flex-wrap items-center gap-2 mt-4 min-w-0 max-w-full">{children}</div>}
    </div>
  );
};

/* Une grande section du tableau de bord : un bloc blanc sur le fond gris de la console,
   ouvert par un bandeau à sa couleur (icône, titre, explication, action à droite). */
export const Bloc = ({ id, titre, aide, Icon, ton = "analyses", action, children, testid }) => {
  const t = TONS_BLOC[ton] || TONS_BLOC.analyses;
  return (
    <section id={id} data-testid={testid} className="rounded-3xl bg-white border overflow-hidden scroll-mt-24"
      style={{ borderColor: t.bord, boxShadow: "0 1px 2px rgba(15,27,45,0.04), 0 8px 24px -12px rgba(15,27,45,0.10)" }}>
      <header className="px-5 md:px-6 py-4 flex flex-wrap items-center gap-3"
        style={{ background: t.doux, borderBottom: `1px solid ${t.bord}` }}>
        {Icon && (
          <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: t.accent, color: "#fff" }}>
            <Icon size={19} />
          </span>
        )}
        {/* Une largeur minimale : sur téléphone, le bouton passe à la ligne au lieu
            d'écraser l'explication en une colonne étroite. */}
        <div className="min-w-[13rem] flex-1">
          <h2 className="font-display text-lg tracking-tight leading-tight" style={{ color: "#0F1B2D" }}>{titre}</h2>
          {aide && <p className="text-xs mt-0.5" style={{ color: t.texte }}>{aide}</p>}
        </div>
        {action && <div className="min-w-0 max-w-full">{action}</div>}
      </header>
      <div className="p-4 md:p-6 space-y-5">{children}</div>
    </section>
  );
};

/* « À ton attention » : ce que l'école a à faire sur cet écran, en haut de la page, comme
   dans Facturation (demandé le 13.10.2026 pour Profs, Attestations, Messages et Assistant
   IA). `points` : [{ id, texte, ton: "rouge" | "ambre", onClick? }]. Rien à faire : rien
   ne s'affiche. */
export const ATonAttention = ({ points, testid = "a-ton-attention" }) => {
  const visibles = (points || []).filter(Boolean);
  if (!visibles.length) return null;
  return (
    <div className="rounded-2xl px-5 py-4" data-testid={testid} style={{ background: "#FFFBEB", border: "1px solid #FDE68A" }}>
      <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: "#92400E" }}>
        <AlertTriangle size={16} style={{ color: "#B45309" }} /> À ton attention
      </div>
      <ul className="mt-2 space-y-1 list-disc pl-5 marker:text-[#B45309]">
        {visibles.map((p) => {
          const couleur = p.ton === "rouge" ? "#B91C1C" : "#92400E";
          return (
            <li key={p.id}>
              {p.onClick ? (
                <button className="text-sm underline decoration-dotted text-left" style={{ color: couleur }}
                  onClick={p.onClick} data-testid={`${testid}-${p.id}`}>{p.texte}</button>
              ) : <span className="text-sm" style={{ color: couleur }} data-testid={`${testid}-${p.id}`}>{p.texte}</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

/* Les groupes de « À traiter » qui concernent un écran (`onglets`), en points pour
   ATonAttention. Les groupes bleus ne sont que des informations (« parties toutes
   seules ») : ils ne demandent rien. `agir(groupe)` : où mène un clic. */
export const pointsATraiter = (aTraiter, onglets, agir) => (aTraiter?.groupes || [])
  .filter((g) => g.nb > 0 && g.gravite !== "bleu"
    && (onglets.includes(g.onglet) || (g.items || []).some((i) => onglets.includes(i.cible?.onglet))))
  .map((g) => ({
    id: g.id, ton: g.gravite === "rouge" ? "rouge" : "ambre",
    texte: `${g.titre} (${g.nb})`,
    onClick: agir ? () => agir(g) : undefined,
  }));

/* Le lien « Ouvrir l'écran → » d'un bandeau de section. */
export const LienBloc = ({ children, onClick, ton = "analyses", testid }) => {
  const t = TONS_BLOC[ton] || TONS_BLOC.analyses;
  return (
    <button onClick={onClick} data-testid={testid}
      className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 bg-white border whitespace-nowrap transition-transform hover:-translate-y-0.5"
      style={{ color: t.texte, borderColor: t.bord }}>
      {children} <ArrowRight size={13} />
    </button>
  );
};

/* Carte teintée avec pastille d'icône — le langage visuel du tableau de bord. */
const TEINTES = {
  bleu: { fond: "#EFF6FF", bord: "#DBEAFE", pastille: "#DBEAFE", icone: "#2563EB", valeur: "#0F1B2D" },
  rose: { fond: "#FDF2F8", bord: "#FBCFE8", pastille: "#FBCFE8", icone: "#DB2777", valeur: "#0F1B2D" },
  vert: { fond: "#F0FDF4", bord: "#BBF7D0", pastille: "#BBF7D0", icone: "#15803D", valeur: "#0F1B2D" },
  violet: { fond: "#F5F3FF", bord: "#DDD6FE", pastille: "#DDD6FE", icone: "#7C3AED", valeur: "#0F1B2D" },
  ambre: { fond: "#FFFBEB", bord: "#FDE68A", pastille: "#FDE68A", icone: "#B45309", valeur: "#B45309" },
  rouge: { fond: "#FEF2F2", bord: "#FECACA", pastille: "#FECACA", icone: "#B91C1C", valeur: "#B91C1C" },
  indigo: { fond: "#EEF2FF", bord: "#C7D2FE", pastille: "#C7D2FE", icone: "#4338CA", valeur: "#0F1B2D" },
  orange: { fond: "#FFF7ED", bord: "#FED7AA", pastille: "#FED7AA", icone: "#C2410C", valeur: "#0F1B2D" },
  sarcelle: { fond: "#F0FDFA", bord: "#99F6E4", pastille: "#99F6E4", icone: "#0F766E", valeur: "#0F1B2D" },
  neutre: { fond: "#FFFFFF", bord: "var(--border)", pastille: "#EEF2F6", icone: "#475569", valeur: "#0F1B2D" },
};

export const CarteIcone = ({ titre, valeur, detail, Icon, teinte = "neutre", onClick, testid, enfants }) => {
  const t = TEINTES[teinte] || TEINTES.neutre;
  return (
    <div
      onClick={onClick}
      data-testid={testid}
      className="rounded-2xl p-5 border relative transition-transform"
      style={{
        background: t.fond, borderColor: t.bord,
        cursor: onClick ? "pointer" : "default",
      }}
      onMouseEnter={(e) => { if (onClick) e.currentTarget.style.transform = "translateY(-3px)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; }}
    >
      <div className="flex items-start gap-4">
        {Icon && (
          <span className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
            style={{ background: t.pastille, color: t.icone }}>
            <Icon size={20} />
          </span>
        )}
        {/* La flèche des cartes cliquables est posée en bas à droite : le texte s'arrête avant. */}
        <div className={`min-w-0${onClick ? " pr-8" : ""}`}>
          <Eyebrow>{titre}</Eyebrow>
          <div className="font-display text-3xl leading-none mt-1.5" style={{ color: t.valeur }}>{valeur}</div>
          {detail && <div className="text-xs text-[var(--muted)] mt-2 leading-snug">{detail}</div>}
          {enfants}
        </div>
      </div>
      {onClick && (
        <span className="absolute right-4 bottom-4 w-8 h-8 rounded-full flex items-center justify-center"
          style={{ background: "#fff", color: t.icone, border: `1px solid ${t.bord}` }}>
          <ArrowRight size={15} />
        </span>
      )}
    </div>
  );
};

/* Carte d'un panneau sombre : même grammaire, fond foncé. */
export const CarteIconeSombre = ({ titre, valeur, detail, Icon, accent = "#7DE8ED", pastille = "#1E3A8A", testid, enfants }) => (
  <div className="rounded-2xl p-5" style={{ background: "#152238", border: "1px solid rgba(255,255,255,0.06)" }} data-testid={testid}>
    <div className="flex items-start gap-4">
      {Icon && (
        <span className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
          style={{ background: pastille, color: "#0F1B2D" }}>
          <Icon size={20} />
        </span>
      )}
      <div className="min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: "rgba(255,255,255,0.55)" }}>{titre}</div>
        <div className="font-display text-3xl leading-none mt-1.5" style={{ color: accent }}>{valeur}</div>
        {detail && <div className="text-xs mt-2 leading-snug" style={{ color: "rgba(255,255,255,0.6)" }}>{detail}</div>}
        {enfants}
      </div>
    </div>
  </div>
);

/* Panneau qui regroupe des cartes sous un intitulé. */
export const Panneau = ({ titre, sombre, children, testid }) => (
  <div className="rounded-3xl p-5" data-testid={testid}
    style={sombre
      ? { background: "#0F1B2D" }
      : { background: "#FBFCFD", border: "1px solid var(--border)" }}>
    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] mb-4 px-1"
      style={{ color: sombre ? "rgba(255,255,255,0.5)" : "var(--muted)" }}>{titre}</div>
    {children}
  </div>
);

export const Carte = ({ titre, valeur, detail, ton = "neutre", testid, onClick }) => {
  const couleurs = {
    neutre: "var(--text)",
    primaire: "var(--primary)",
    rouge: GRAVITES.rouge.texte,
    ambre: GRAVITES.ambre.texte,
    vert: GRAVITES.vert.texte,
  };
  // Cliquable (`onClick`) : la carte mène à l'écran qui détaille son chiffre.
  const Racine = onClick ? "button" : "div";
  return (
    <Racine
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`bg-white rounded-2xl p-5 border transition-shadow hover:shadow-[var(--shadow-sm)] text-left w-full${onClick ? " cursor-pointer" : ""}`}
      style={{ borderColor: "var(--border)" }}
      data-testid={testid}
    >
      <Eyebrow>{titre}</Eyebrow>
      <div className="font-display text-3xl leading-none mt-2" style={{ color: couleurs[ton] }}>{valeur}</div>
      {detail && <div className="text-xs text-[var(--muted)] mt-2 leading-snug">{detail}</div>}
    </Racine>
  );
};

export const CarteSombre = ({ titre, valeur, detail, accent = "#7DE8ED", testid }) => (
  <div className="rounded-2xl p-5" style={{ background: "#0F1B2D", color: "#fff" }} data-testid={testid}>
    <div className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: "rgba(255,255,255,0.55)" }}>{titre}</div>
    <div className="font-display text-3xl leading-none mt-2" style={{ color: accent }}>{valeur}</div>
    {detail && <div className="text-xs mt-2 leading-snug" style={{ color: "rgba(255,255,255,0.6)" }}>{detail}</div>}
  </div>
);

export const Badge = ({ ton = "bleu", children, testid }) => {
  const g = GRAVITES[ton] || GRAVITES.bleu;
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border whitespace-nowrap"
      style={{ background: g.fond, borderColor: g.bord, color: g.texte }}
      data-testid={testid}
    >
      {children}
    </span>
  );
};

/* `surligne` : la ligne qu'on vient ouvrir depuis « À traiter ». Elle est mise en
   évidence et amenée à l'écran, au milieu des autres lignes de la même liste. */
export const Tableau = ({ entetes, lignes, rendu, vide = "Rien à afficher.", testid, onClic, fixe = false, surligne }) => (
  <div className="bg-white rounded-2xl border overflow-hidden" style={{ borderColor: "var(--border)" }} data-testid={testid}>
    <div className="overflow-x-auto">
      {/* Sur téléphone, on préfère un tableau qui glisse horizontalement plutôt que des
          colonnes écrasées : les noms de cours restent lisibles d'un coup d'œil. */}
      <table className={`w-full text-sm min-w-[680px] md:min-w-0${fixe ? " table-fixed" : ""}`}>
        <thead>
          <tr style={{ background: "#F1F5F9" }}>
            {entetes.map((h, i) => (
              <th
                key={i}
                className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.1em] text-[#475569] whitespace-nowrap"
                style={{ textAlign: typeof h === "object" && h.droite ? "right" : "left",
                         // Une colonne « large » absorbe l'espace libre : sans elle, la place
                         // restante se répartissait partout et laissait de grands vides.
                         // `largeur` est une largeur souhaitée : elle se comprime si l'écran
                         // est étroit, au lieu de couper la colonne.
                         width: typeof h === "object"
                           ? (h.large ? "100%" : h.largeur || undefined) : undefined }}
              >
                {typeof h === "object" ? h.label : h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lignes.length === 0 && (
            <tr><td className="px-4 py-8 text-[var(--muted)]" colSpan={entetes.length}>{vide}</td></tr>
          )}
          {lignes.map((l, k) => {
            const visee = surligne ? surligne(l) : false;
            const fond = visee ? "#FEF9C3" : (k % 2 ? "#FBFCFD" : "#fff");
            return (
            <tr
              key={l.id || k}
              ref={visee ? (el) => el?.scrollIntoView({ block: "center", behavior: "smooth" }) : undefined}
              data-testid={visee ? "ligne-visee" : undefined}
              onClick={onClic ? () => onClic(l) : undefined}
              className="border-t transition-colors"
              style={{
                borderColor: "var(--border-soft)",
                background: fond,
                boxShadow: visee ? "inset 3px 0 0 #F59E0B" : undefined,
                cursor: onClic ? "pointer" : "default",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "var(--primary-tint)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = fond; }}
            >
              {rendu(l)}
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

export const Cellule = ({ children, droite, fort, muet, testid, largeur }) => (
  <td
    className={`px-4 py-3 ${fort ? "font-semibold" : ""} ${muet ? "text-[var(--muted)]" : ""}`}
    style={{ textAlign: droite ? "right" : "left", minWidth: largeur }}
    data-testid={testid}
  >
    {children}
  </td>
);

export const Barre = ({ pourcent }) => (
  <div className="flex items-center gap-2">
    <div className="h-1.5 rounded-full flex-1 min-w-[60px]" style={{ background: "#E2E8F0" }}>
      <div
        className="h-1.5 rounded-full"
        style={{
          width: `${Math.min(pourcent, 100)}%`,
          background: pourcent >= 85 ? GRAVITES.rouge.puce : pourcent >= 50 ? "var(--primary)" : GRAVITES.ambre.puce,
        }}
      />
    </div>
    <span className="text-xs tabular-nums w-9 text-right">{pourcent} %</span>
  </div>
);

export const AVenir = ({ titre, points }) => (
  <div className="bg-white rounded-2xl p-8 border max-w-3xl" style={{ borderColor: "var(--border)" }} data-testid="console-a-venir">
    <Eyebrow>Étape suivante</Eyebrow>
    <h1 className="font-display text-3xl tracking-tight mt-2">{titre}</h1>
    <p className="text-sm text-[var(--muted)] mt-3">Écran prévu, pas encore construit. Ce qu'il contiendra :</p>
    <ul className="mt-4 space-y-2 text-sm">
      {points.map((p) => (
        <li key={p} className="flex gap-2"><span style={{ color: "var(--primary)" }}>▸</span><span>{p}</span></li>
      ))}
    </ul>
  </div>
);

export const Case = ({ cochee, onChange, testid, titre }) => (
  <button
    type="button"
    onClick={(e) => { e.stopPropagation(); onChange(!cochee); }}
    data-testid={testid}
    title={titre}
    className="w-[18px] h-[18px] rounded-[5px] border flex items-center justify-center shrink-0 transition-colors"
    style={{
      borderColor: cochee ? "var(--primary)" : "#CBD5E1",
      background: cochee ? "var(--primary)" : "#fff",
    }}
  >
    {cochee && (
      <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
        <path d="M2 6.5L4.5 9L10 3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )}
  </button>
);

export const Modale = ({ titre, sous_titre, onFermer, children, testid, large }) => (
  <div className="fixed inset-0 z-50 flex items-start justify-center px-4 py-10 overflow-auto" style={{ background: "rgba(15,27,45,0.55)" }}>
    <div
      className="bg-white rounded-2xl p-6 w-full"
      style={{ maxWidth: large ? 720 : 520 }}
      data-testid={testid}
    >
      <div className="flex items-start gap-4">
        <div>
          <h2 className="font-display text-xl tracking-tight">{titre}</h2>
          {sous_titre && <p className="text-xs text-[var(--muted)] mt-1">{sous_titre}</p>}
        </div>
        <button onClick={onFermer} className="ml-auto text-[var(--muted)] hover:text-[var(--text)]" data-testid="modale-fermer">✕</button>
      </div>
      <div className="mt-5">{children}</div>
    </div>
  </div>
);

export const ChampTexte = ({ label, valeur, onChange, type = "text", testid, aide }) => (
  <label className="block mb-4">
    <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">{label}</span>
    <input
      className="input-field mt-1"
      type={type}
      value={valeur || ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
    />
    {aide && <span className="text-xs text-[var(--muted)]">{aide}</span>}
  </label>
);

/* Une option peut porter un `groupe` (ex. le jour d'un cours) : les options qui se suivent
   avec le même groupe sont réunies sous un intitulé dans la liste déroulante. */
const optionsGroupees = (options) => options.reduce((acc, o) => {
  const groupe = o?.groupe || "";
  const dernier = acc[acc.length - 1];
  if (dernier && dernier.groupe === groupe) dernier.options.push(o);
  else acc.push({ groupe, options: [o] });
  return acc;
}, []);

const UneOption = ({ o }) => <option value={o.valeur ?? o}>{o.label ?? o}</option>;

export const ChampChoix = ({ label, valeur, options, onChange, testid, aide }) => (
  <label className="block mb-4">
    <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">{label}</span>
    <select
      className="input-field mt-1"
      value={valeur || ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
    >
      {optionsGroupees(options).flatMap(({ groupe, options: liste }, n) => (groupe ? [
        <optgroup key={`groupe-${n}`} label={groupe}>
          {liste.map((o) => <UneOption key={o.valeur ?? o} o={o} />)}
        </optgroup>,
      ] : liste.map((o) => <UneOption key={o.valeur ?? o} o={o} />)))}
    </select>
    {aide && <span className="block text-[11px] text-[var(--muted)] mt-1">{aide}</span>}
  </label>
);

/* Plusieurs valeurs pour un même champ (les cours de comédie musicale se partagent
   entre professeurs) : des pastilles à cocher, enregistrées « Noémie, Camille ». */
export const ChampMultiple = ({ label, valeur, options, onChange, testid, aide }) => {
  // L'école écrit « Noémie - Roxane » au classeur et coche deux pastilles ici : la même
  // lecture des deux côtés, et on réécrit toujours au format « A - B » attendu par le
  // site et par l'onglet Listes (sinon : professeur « inconnu » et fausse alerte).
  const decouper = (v) => String(v || "").split(/\s*[,;/&]\s*|\s+-\s+/).map((s) => s.trim()).filter(Boolean);
  const choisis = decouper(valeur);
  const basculer = (o) => onChange(
    (choisis.includes(o) ? choisis.filter((x) => x !== o) : [...choisis, o]).join(" - "));
  return (
    <div className="block mb-4">
      <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">{label}</span>
      <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={testid}>
        {options.filter(Boolean).map((o) => {
          const actif = choisis.includes(o);
          return (
            <button
              key={o}
              type="button"
              onClick={() => basculer(o)}
              data-testid={`${testid}-${o}`}
              className="px-3 py-1.5 rounded-full text-xs transition-transform hover:-translate-y-0.5"
              style={{
                background: actif ? "var(--primary)" : "#fff",
                color: actif ? "#04202A" : "var(--muted)",
                border: `1px solid ${actif ? "var(--primary)" : "var(--border)"}`,
                fontWeight: actif ? 600 : 400,
              }}
            >
              {o}
            </button>
          );
        })}
      </div>
      {aide && <span className="text-xs text-[var(--muted)] block mt-1.5">{aide}</span>}
    </div>
  );
};


export const BarreSelection = ({ nb, children, testid }) => (
  <div
    className="sticky top-[68px] z-20 flex flex-wrap items-center gap-3 px-4 py-3 rounded-2xl"
    style={{ background: "#0F1B2D", color: "#fff" }}
    data-testid={testid}
  >
    <span className="text-sm font-semibold">{nb} sélectionné(s)</span>
    <div className="ml-auto flex flex-wrap gap-2">{children}</div>
  </div>
);

export const BoutonSombre = ({ children, onClick, testid, danger, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold transition-transform hover:-translate-y-0.5 disabled:opacity-50"
    style={{ background: danger ? "#DC2626" : "var(--primary)", color: danger ? "#fff" : "#04202A" }}
  >
    {children}
  </button>
);

/* Impression en paysage (listes de stage, d'audition, de spectacle et de workshop, affiche
   du planning). Tant que l'écran est ouvert, le format de TOUTES les pages devient A4
   paysage. Une page nommée (`page: paysage` en CSS) ne suffisait pas : dès qu'un élément
   imprimable la précédait (le sous-menu « Listes / Cours privés »), Chrome sortait d'abord
   une page portrait presque vide, puis mettait la liste en page à la largeur du portrait
   — elle n'occupait que les deux tiers de la feuille paysage (école, 09.10.2026). */
const STYLE_PAYSAGE = "@media print { @page { size: A4 landscape; margin: 10mm 9mm; } }";

export const usePagePaysage = (actif = true) => {
  useEffect(() => {
    if (!actif) return undefined;
    // Ajoutée après les feuilles de style du site, la règle l'emporte sur le portrait
    // par défaut d'index.css ; elle disparaît en quittant l'écran.
    const style = document.createElement("style");
    style.setAttribute("data-impression", "paysage");
    style.textContent = STYLE_PAYSAGE;
    document.head.appendChild(style);
    return () => style.remove();
  }, [actif]);
};
